import { FormGroup, Stack, TextField, Typography } from "@mui/material";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ScoreRange } from "../../../../types/actionModule/rewardCalculationType/rewardCalculationType";
import { RewardCalculationTypeEditorProps } from "../../../../types/actionModule/editor/rewardCalculationTypeEditorTypes";
import { Add, Delete } from "@mui/icons-material";
import { GeneralButton } from "../../../../common/ui/GeneralButton";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { isScoreRangeRewardCalculationTypeModel, ScoreRangeRewardCalculationTypeModel } from "../../../../types/actionModule/rewardCalculationType/scoreRangeRewardCalculationType";

export default function ScoreRangeRewardCalculationTypeEditor(props: RewardCalculationTypeEditorProps) {
    const { t } = useTranslation();

    const calculationType = props.value;

    if (!isScoreRangeRewardCalculationTypeModel(calculationType)) {
        return <>Invalid value</>
    }

    const scoreRanges = calculationType.scoreRanges ?? [];

    return InnerScoreRangeRewardCalculationTypeEditor(scoreRanges, props, calculationType, t);
}

interface ScoreRangeEditorProps {
    range: ScoreRange;
    onChange: (range: ScoreRange) => void;
    onDelete: () => void;
    isFirst: boolean;
}

function InnerScoreRangeRewardCalculationTypeEditor(scoreRanges: ScoreRange[], props: RewardCalculationTypeEditorProps, calculationType: ScoreRangeRewardCalculationTypeModel, t: TFunction<"translation", undefined>) {
    const onChangeScoreRange = useCallback((newRange: ScoreRange, originalRange: ScoreRange) => {
        const updatedRanges = scoreRanges.map((sr) => sr === originalRange ? newRange : sr);

        updatedRanges.sort((a, b) => a.scoreTo - b.scoreTo);

        for (let i = 1; i < scoreRanges.length; i++) {
            const previousRange = updatedRanges[i - 1];
            const range = updatedRanges[i];

            updatedRanges[i] = {
                ...range,
                scoreFrom: previousRange.scoreTo,
            };
        }

        props.onChanged({
            ...calculationType,
            scoreRanges: updatedRanges
        } as ScoreRangeRewardCalculationTypeModel);
    }, [scoreRanges]);

    const onAddScoreRange = useCallback(() => {
        props.onChanged({
            ...calculationType,
            scoreRanges: [
                ...scoreRanges,
                {
                    scoreFrom: 0,
                    scoreTo: 0,
                    pointsRewarded: 0,
                } as ScoreRange
            ]
        } as ScoreRangeRewardCalculationTypeModel);

    }, [scoreRanges]);

    const onDeleteScoreRange = useCallback((deletedRange: ScoreRange) => {
        props.onChanged({
            ...calculationType,
            scoreRanges: [
                ...scoreRanges.filter((sr) => sr !== deletedRange)
            ]
        } as ScoreRangeRewardCalculationTypeModel);
    }, [scoreRanges]);

    return <Stack direction="column">
        <Typography variant="caption">{t("actionModules.rewardCalculationType.scoreRange.description")}</Typography>
        <Typography variant="subtitle2" sx={{ pb: 1, pt: 2 }}>{t("actionModules.rewardCalculationType.scoreRange.scoreRanges")}</Typography>
        <Stack direction="column" sx={{ gap: 1 }}>
            {scoreRanges.map((sr, i) => <ScoreRangeEditor
                key={`${sr.scoreFrom}_${sr.scoreTo}_${sr.pointsRewarded}`}
                range={sr}
                onChange={(value) => onChangeScoreRange(value, sr)}
                onDelete={() => onDeleteScoreRange(sr)}
                isFirst={i === 0} />)}
            <GeneralButton appVariant="icon" onClick={() => onAddScoreRange()}>
                <Add /> {t("actionModules.rewardCalculationType.scoreRange.add")}
            </GeneralButton>
        </Stack>
    </Stack>;
}

function ScoreRangeEditor(props: ScoreRangeEditorProps) {
    const { t } = useTranslation();

    const key = `${props.range.scoreFrom}_${props.range.scoreTo}_${props.range.pointsRewarded}`;

    return <Stack direction="row" sx={{gap: 2, alignItems: "center"}}>
        <div style={{ flex: 1, justifySelf: "end", textAlign: "end" }}>
            {
                props.isFirst
                    ? <FormGroup sx={{ flex: 1 }}>
                        <TextField
                            key={`${key}_from`}
                            defaultValue={props.range.scoreFrom}
                            onBlur={(e) => props.onChange({
                                ...props.range,
                                scoreFrom: Number(e.target.value)
                            })}
                            size="small"
                            fullWidth
                            label={t("actionModules.rewardCalculationType.scoreRange.from")} />
                    </FormGroup>
                    : props.range.scoreFrom
            }
        </div>
        <Stack direction="row" sx={{gap: 2}}>
            <Typography>–</Typography>
            <Typography>{"<"}</Typography>
        </Stack>
        <div style={{ flex: 1 }}>
            <FormGroup sx={{ flex: 1 }}>
                <TextField
                    key={`${key}_to`}
                    defaultValue={props.range.scoreTo}
                    onBlur={(e) => props.onChange({
                        ...props.range,
                        scoreTo: Number(e.target.value)
                    })}
                    size="small"
                    fullWidth
                    label={t("actionModules.rewardCalculationType.scoreRange.to")} />
            </FormGroup>
        </div>
        <div>
            <ArrowForwardIcon />
        </div>
        <div style={{ flex: 1 }}>
            <FormGroup sx={{ flex: 1 }}>
                <TextField
                    key={`${key}_points`}
                    defaultValue={props.range.pointsRewarded}
                    onBlur={(e) => props.onChange({
                        ...props.range,
                        pointsRewarded: Number(e.target.value)
                    })}
                    size="small"
                    fullWidth
                    label={t("actionModules.rewardCalculationType.scoreRange.reward")} />
            </FormGroup>
        </div>
        <GeneralButton appVariant="ghost" onClick={() => props.onDelete()}>
            <Delete />
        </GeneralButton>
    </Stack>

}